/**
 * AnalizGecmisiPage - Analysis History Page
 * 
 * Left panel: History list (former sidebar content)
 * Right panel: Selected analysis details (AnalysisResults format)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQueryHistory } from '../hooks/useQueryHistory';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  TrendingUp, 
  Loader2, 
  Trash2, 
  CheckSquare, 
  Square,
  User,
  Instagram,
  Eye,
  Heart,
  MessageCircle,
  Calendar,
  ArrowLeft,
  Search,
  BarChart3
} from "lucide-react";
import { toast } from 'sonner';

// Navbar Component (Tabs)
import NavTabs from '../components/NavTabs';
import Footer from '../components/Footer';

// AnalysisResults component - Ana sayfadaki aynı format
import { AnalysisResults } from '../components/HomePage';

/**
 * formatNumberShort - Kısa sayı formatı (1.2K, 3.5M)
 * v13.7: Full null-safety
 */
const formatNumberShort = (num) => {
  // v13.7 CRASH DEFENSE: Handle all edge cases
  if (num == null) return "0";
  
  // v13.7: Handle string inputs with comma separators
  let parsedNum = num;
  if (typeof num === 'string') {
    const cleaned = num.replace(/,/g, '').replace(/\s/g, '').trim();
    parsedNum = parseFloat(cleaned);
    if (isNaN(parsedNum)) return "0";
  }
  
  if (typeof parsedNum !== 'number' || isNaN(parsedNum)) return "0";
  
  if (parsedNum >= 1000000) {
    return (parsedNum / 1000000).toFixed(1) + "M";
  }
  if (parsedNum >= 1000) {
    const kValue = parsedNum / 1000;
    if (kValue < 10) {
      return kValue.toFixed(1) + "K";
    }
    return Math.floor(kValue) + "K";
  }
  return Math.round(parsedNum).toString();
};

/**
 * formatNumber - Tam sayı formatı (1,234,567)
 * v13.7: Full null-safety
 */
const formatNumber = (num) => {
  // v13.7 CRASH DEFENSE: Handle all edge cases
  if (num == null) return "0";
  
  // v13.7: Handle string inputs with comma separators
  let parsedNum = num;
  if (typeof num === 'string') {
    const cleaned = num.replace(/,/g, '').replace(/\s/g, '').trim();
    parsedNum = parseFloat(cleaned);
    if (isNaN(parsedNum)) return "0";
  }
  
  if (typeof parsedNum !== 'number' || isNaN(parsedNum)) return "0";
  
  return Math.round(parsedNum).toLocaleString('tr-TR');
};

/**
 * getInitials - Avatar için baş harfler
 */
const getInitials = (username) => {
  if (!username) return '?';
  const name = username.replace('@', '').toUpperCase();
  return name.length >= 2 ? name.substring(0, 2) : name[0];
};

/**
 * AnalysisCard - Sol paneldeki analiz kartı
 */
const AnalysisCard = ({
  query,
  isSelected,
  isActive,
  onToggleSelection,
  onClick,
  isOwner,
  runByName,
  showRunner,
  t
}) => {
  return (
    <div
      data-testid={`history-item-${query.username}`}
      className={`group flex gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
        isActive 
          ? 'bg-[#FFDD59]/10 border-[#FFDD59]/50' 
          : 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800 hover:border-gray-600'
      }`}
      onClick={onClick}
    >
      {/* Checkbox - havuz: yalniz kendi kaydini secip silebilir (soluk/pasif, GRUP-3 deseni: goster ama kapat) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (isOwner === false) return;  // savunma: gorsel disabled tek basina yetmez (klavye/programatik)
          onToggleSelection(query.id);
        }}
        disabled={isOwner === false}
        title={isOwner === false ? t('history.cannot_delete_other') : undefined}
        className={`p-1 rounded transition-colors self-start mt-1 ${isOwner === false ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/10'}`}
        data-testid={`history-checkbox-${query.username}`}
      >
        {isSelected ? (
          <CheckSquare className="w-5 h-5 text-[#FFDD59]" />
        ) : (
          <Square className="w-5 h-5 text-gray-500" />
        )}
      </button>

      {/* Avatar */}
      <div className="shrink-0 w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-gray-600">
        {query.profile_picture ? (
          <img 
            src={query.profile_picture}
            alt={query.username}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <span 
          className="font-bold text-[#FFDD59]"
          style={query.profile_picture ? {display: 'none', fontSize: '14px'} : {fontSize: '14px'}}
        >
          {getInitials(query.username)}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-semibold text-white truncate" style={{ fontSize: '14px' }}>
            @{query.username}
          </h4>
          <span className="text-gray-500 whitespace-nowrap ml-2" style={{ fontSize: '11px' }}>
            {query.time_ago}
          </span>
        </div>

        {/* Havuz: kim calistirdi - yalniz birden fazla farkli calistiran varsa (tek kisilik havuzda gizli) */}
        {showRunner && (
          <p className="text-gray-500 truncate mb-1" style={{ fontSize: '11px' }}>
            {t('history.run_by')}: {runByName || t('history.unknown_user')}
          </p>
        )}

        <div className="flex items-center gap-3 text-gray-400" style={{ fontSize: '13px' }}>
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {formatNumberShort(query.follower_count)}
          </span>
          {query.expected_views_avg && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {formatNumberShort(query.expected_views_avg)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * AnalysisDetail - Right panel analysis detail
 * Uses AnalysisResults component to show same format as home page
 * v13.7: Full null-safety
 */
const AnalysisDetail = ({ analysis, t }) => {
  if (!analysis) {
    return (
      <div className="hidden lg:flex flex-1 items-center justify-center bg-gray-900/50 rounded-2xl border border-gray-800">
        <div className="text-center p-8">
          <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-400 mb-2" style={{ fontSize: '17px' }}>
            {t('history.select_analysis')}
          </h3>
          <p className="text-gray-500" style={{ fontSize: '14px' }}>
            {t('history.select_description')}
          </p>
        </div>
      </div>
    );
  }

  // v13.7 CRASH DEFENSE: Comprehensive null-safety for result mapping
  // useQueryHistory'den gelen mappedResult zaten düzleştirilmiş format
  // result_data içinde değil, doğrudan analysis objesi içinde
  
  // v13.7: Multiple fallback paths for result data
  let resultForDisplay = null;
  
  if (analysis?.username) {
    // Already mapped format
    resultForDisplay = analysis;
  } else if (analysis?.result_data && typeof analysis.result_data === 'object') {
    // Need to extract from result_data
    resultForDisplay = analysis.result_data;
  } else if (typeof analysis === 'object') {
    // Fallback: use analysis directly
    resultForDisplay = analysis;
  }
  
  // v13.7: Final validation - ensure we have something to display
  if (!resultForDisplay || (!resultForDisplay.username && !resultForDisplay.profile_pic_url)) {
    return (
      <div className="flex-1 bg-gray-900/50 rounded-2xl border border-gray-800 p-8" data-testid="analysis-detail-error">
        <div className="text-center">
          <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-400 mb-2" style={{ fontSize: '17px' }}>
            {t('history.data_unavailable') || 'Veri Bulunamadı'}
          </h3>
          <p className="text-gray-500" style={{ fontSize: '14px' }}>
            {t('history.data_unavailable_desc') || 'Bu analiz için veri mevcut değil.'}
          </p>
        </div>
      </div>
    );
  }

  // v13.9.2: Extract currency from analysis; yoksa AnalysisResults locale-default uygular (hardcoded TRY DEGIL)
  const currency = analysis?.currency || resultForDisplay?.currency || null;

  return (
    <div className="flex-1 bg-gray-900/50 rounded-2xl border border-gray-800" data-testid="analysis-detail">
      {/* v7.1: max-w-[50rem] ile ana sayfadaki gibi sabit genişlik, mx-auto ile ortalama */}
      <div className="max-w-[50rem] mx-auto p-2 sm:p-4">
        {/* AnalysisResults component'ini kullan - ana sayfadaki aynı format */}
        <AnalysisResults result={resultForDisplay} currency={currency} />
      </div>
    </div>
  );
};

/**
 * AnalizGecmisiPage - Main page component
 */
export default function AnalizGecmisiPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { t, locale } = useTranslation();
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Helper: Get locale-aware paths for navigation
  const getLocalePath = (route) => {
    const routes = {
      en: { login: '/en/login', history: '/en/history' },
      tr: { login: '/tr/giris', history: '/tr/analizgecmisi' },
      ar: { login: '/ar/login', history: '/ar/history' },
      fr: { login: '/fr/login', history: '/fr/history' },
      de: { login: '/de/login', history: '/de/history' },
      es: { login: '/es/login', history: '/es/history' }
    };
    return routes[locale]?.[route] || routes.en[route];
  };

  // Query History Hook
  const {
    queryHistory,
    filteredHistory,
    loadingHistory,
    searchQuery,
    setSearchQuery,
    selectedQueryIds,
    toggleQuerySelection,
    selectAllQueries,
    clearSelectedQueries,
    deleteSelectedQueries,
    handleHistoryClick,
    getQueryHistoryDuration
  } = useQueryHistory({
    onHistoryClick: (query, mappedResult) => {
      setSelectedAnalysis(mappedResult);
    }
  });

  // HAVUZ: "kim calistirdi" satiri YALNIZ birden fazla farkli calistiran varsa gosterilir.
  //   Tek kisilik havuzda (takim-disi kullanici, %99 vaka) tum satirlar is_own -> gizli, EKRAN DEGISMEZ.
  const hasMultipleRunners = queryHistory.some(q => q.is_own === false);
  // "Tumunu Sec" yalniz SAHIP-OLUNAN kayitlari kapsar (havuz: secilemez satirlar sayima girmez).
  const ownableCount = queryHistory.filter(q => q.is_own !== false).length;

  // Auth check
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(getLocalePath('login'));
    }
  }, [user, authLoading, navigate, locale]);

  // Handle bulk delete
  const handleBulkDelete = async () => {
    await deleteSelectedQueries();
    setShowBulkDeleteConfirm(false);
    setSelectedAnalysis(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FFDD59] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Navigation Tabs */}
      <NavTabs user={user} currentPage="analizgecmisi" />

      {/* Main Content - Responsive Layout with matched heights */}
      <div className="flex-1 flex flex-col lg:flex-row lg:items-start lg:p-4 lg:gap-4 lg:max-w-[1600px] lg:mx-auto">
        
        {/* Left Panel - History List (Always visible on desktop, hidden on mobile when detail selected) */}
        <div className={`
          w-full lg:w-80 xl:w-96 lg:flex-shrink-0
          border-b lg:border-b-0 lg:border lg:border-gray-800 lg:rounded-2xl
          bg-gray-900/30 flex flex-col
          ${selectedAnalysis ? 'hidden lg:flex lg:max-h-[800px]' : 'flex'}
        `}>
          {/* Header */}
          <div className="p-4 border-b border-gray-800">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#FFDD59]" />
                <h2 className="font-semibold text-white" style={{ fontSize: '17px' }}>{t('history.title')}</h2>
              </div>
              <span className="text-gray-500" style={{ fontSize: '13px' }}>
                {getQueryHistoryDuration()}
              </span>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('history.search_placeholder')}
                className="pl-10 bg-gray-800 border-gray-700 text-white"
                style={{ fontSize: '14px' }}
                data-testid="history-search-input"
              />
            </div>

            {/* Bulk Actions */}
            {selectedQueryIds.length > 0 && (
              <div className="mt-3 flex gap-2">
                <Button
                  onClick={() => setShowBulkDeleteConfirm(true)}
                  size="sm"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  style={{ fontSize: '13px' }}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  {t('history.delete_selected')} ({selectedQueryIds.length})
                </Button>
                <Button
                  onClick={selectAllQueries}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
                  style={{ fontSize: '13px' }}
                  data-testid="select-all-btn"
                >
                  {ownableCount > 0 && selectedQueryIds.length === ownableCount ? t('history.deselect_all') : t('history.select_all')}
                </Button>
              </div>
            )}
          </div>

          {/* History List - OPARA v8.0: maxHeight artırıldı (8 -> 11 kutucuk için) */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ maxHeight: '900px' }}>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#FFDD59] animate-spin" />
              </div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-center py-12">
                <Instagram className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500" style={{ fontSize: '14px' }}>
                  {t('history.no_results')}
                </p>
              </div>
            ) : (
              filteredHistory.map((query) => (
                <AnalysisCard
                  key={query.id}
                  query={query}
                  isSelected={selectedQueryIds.includes(query.id)}
                  isActive={selectedAnalysis?.id === query.id}
                  onToggleSelection={toggleQuerySelection}
                  onClick={() => handleHistoryClick(query)}
                  isOwner={query.is_own}
                  runByName={query.run_by_name}
                  showRunner={hasMultipleRunners}
                  t={t}
                />
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Analysis Detail (Hidden on mobile when no selection, full screen on mobile when selected) */}
        <div className={`
          flex-1 lg:max-h-[800px] lg:overflow-hidden
          ${selectedAnalysis ? 'flex flex-col' : 'hidden lg:flex lg:flex-col'}
        `}>
          {/* Mobile Back Button */}
          {selectedAnalysis && (
            <div className="lg:hidden p-4 border-b border-gray-800 bg-gray-900/50">
              <Button
                onClick={() => setSelectedAnalysis(null)}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('common.back')}
              </Button>
            </div>
          )}
          {/* OPARA v8.0: İçerik scroll edilebilir, scrollbar gizli */}
          <div className="flex-1 p-4 lg:p-6 overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <AnalysisDetail analysis={selectedAnalysis} t={t} />
          </div>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">
              {t('history.delete_selected')}
            </h3>
            <p className="text-gray-400 mb-6">
              {selectedQueryIds.length} {locale === 'tr' ? 'analizi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.' : 'analysis will be deleted. This action cannot be undone.'}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowBulkDeleteConfirm(false)}
                variant="outline"
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                {t('bulk.cancel')}
              </Button>
              <Button
                onClick={handleBulkDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {t('history.delete_selected')}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer - Same across all pages */}
      <Footer />
      
      {/* Language Sidebar */}
      {/* Language Sidebar removed */}
    </div>
  );
}
